import { CrudColumn } from './CrudTable';
import { CsvColumn } from './exportCsv';
import { warnInDev } from './warnInDev';

/**
 * Resolves a reference column's label from the shared option cache, or null when the cache
 * has no entry — the list was never rendered, or the id is stale.
 */
export type ReferenceLookup = (column: CrudColumn<any>, id: string) => string | null;

export interface CsvColumnOptions {
  translate: (key: string, vars?: Record<string, unknown>) => string;
  lookupReference: ReferenceLookup;
}

const raw = (row: unknown, key: string): string => {
  const value = (row as Record<string, unknown>)[key];
  return value == null ? '' : String(value);
};

/**
 * Maps the table's columns to CSV columns (spec: 2026-09-10 §1).
 *
 * The destination is a spreadsheet, not a reader, so numbers and dates go out RAW rather
 * than in the table's display form: `45K` and `10/09/2026` are reading conventions that
 * break sorting and arithmetic on the other side. Labels — tags and references — are the
 * opposite case: their stored value is an opaque id or code, so the label is the useful
 * thing and the id is not.
 */
export function toCsvColumns<TEntity>(columns: CrudColumn<TEntity>[], options: CsvColumnOptions): CsvColumn<TEntity>[] {
  return columns.map((column) => ({
    header: options.translate(column.headerKey),
    value: (row: TEntity) => cellValue(column, row, options),
  }));
}

function cellValue<TEntity>(column: CrudColumn<TEntity>, row: TEntity, options: CsvColumnOptions): string {
  // An explicit serialiser always wins, including over `display`.
  if (column.toCsv) {
    return column.toCsv(row);
  }

  if (column.display) {
    switch (column.display.kind) {
      case 'boolean': {
        const value = (row as Record<string, unknown>)[column.key];
        return value == null ? '' : String(Boolean(value));
      }
      case 'date': {
        const value = (row as Record<string, unknown>)[column.key];
        if (value == null || value === '') {
          return '';
        }
        const date = new Date(value as string | number | Date);
        // An unparseable date exports as its raw text rather than "Invalid Date".
        return Number.isNaN(date.getTime()) ? String(value) : date.toISOString();
      }
      case 'tag': {
        const value = raw(row, column.key);
        const entry = column.display.map[value];
        return entry ? options.translate(entry.labelKey) : value;
      }
      case 'reference': {
        const id = raw(row, column.key);
        if (!id) {
          return '';
        }
        return options.lookupReference(column as CrudColumn<any>, id) ?? id;
      }
      // currency and number are deliberately raw; text has no transformation to apply.
      default:
        return raw(row, column.key);
    }
  }

  if (column.render) {
    // `render` returns React nodes, which cannot be flattened into a cell honestly. The raw
    // value is the best guess available; a column that needs better supplies `toCsv`.
    warnInDev(
      `column "${column.key}" renders markup and has no \`toCsv\`, so its export falls back ` +
        'to the raw field value.'
    );
  }

  return raw(row, column.key);
}
